import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Body, Heading, Title, Small } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import Chip from '@/components/Chip';
import LoadingState from '@/components/LoadingState';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { TableRow, TableCell } from '@/elements/Table';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type LogEntry = {
  id: number;
  timestamp: string;
  action: string;
  column_changed: string;
  old_value: {
    raw: string;
    formatted: string;
    relative: string;
  };
  new_value: {
    raw: string;
    formatted: string;
    relative: string;
  };
  change_summary: string;
  changed_by: {
    id: number;
    name: string;
    type: string;
    email: string;
  };
  request_context: {
    request_id: string;
    ip_address: string;
    user_agent: string;
  };
  severity: string;
  category: string;
};

type TimesheetInfo = {
  id: number;
  worker: {
    id: number;
    name: string;
  };
  job: {
    id: number;
    title: string;
  };
  account: {
    id: number;
    name: string;
  };
  current_status: {
    approved: boolean;
    invoiced: boolean;
    flagged: boolean;
  };
  time_tracking?: {
    original?: {
      checkin_at?: string | null;
      checkout_at?: string | null;
    };
    reported?: {
      checkin_at?: string | null;
      checkout_at?: string | null;
      break_minutes?: number;
    };
    approved?: {
      checkin_at?: string | null;
      checkout_at?: string | null;
      break_minutes?: number;
      total_minutes?: number;
      approved_at?: string | null;
    };
    modified?: {
      checkin_at?: string | null;
      checkout_at?: string | null;
      break_minutes?: number;
    };
  };
};

type LogSummary = {
  total_changes: number;
  recent_changes_24h: number;
  critical_changes: number;
  most_changed_column: string;
  changes_by_action: {
    create: number;
    update: number;
  };
  changes_by_user_count: {
    [userId: string]: number;
  };
  first_change: string;
  last_change: string;
};

type TimesheetLogsResponse = {
  status: string;
  timestamp: string;
  timesheet_id: number;
  timesheet_info: TimesheetInfo;
  total_changes: number;
  logs: LogEntry[];
  summary: LogSummary;
};

type ModalProps = {
  timesheetId?: number | null;
  isOpen: boolean;
  onClose: () => void;
};

const LOG_HEADER_FIELDS = [
  { name: 'TIMESTAMP', style: { width: '15%' } },
  { name: 'ACTION', style: { width: '10%' } },
  { name: 'COLUMN CHANGED', style: { width: '15%' } },
  { name: 'CHANGE SUMMARY', style: { width: '30%' } },
  { name: 'CHANGED BY', style: { width: '15%' } },
  { name: 'SEVERITY', style: { width: '10%' } },
  { name: 'CATEGORY', style: { width: '15%' } },
];

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  } catch (e) {
    return dateString;
  }
};

const getSeverityChip = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'critical':
      return <Chip color="error" label="Critical" size="small" />;
    case 'high':
      return <Chip color="warning" label="High" size="small" />;
    case 'medium':
      return <Chip color="default" label="Medium" size="small" />;
    case 'low':
      return <Chip color="success" label="Low" size="small" />;
    default:
      return <Chip color="default" label={severity} size="small" />;
  }
};

const getActionChip = (action: string) => {
  switch (action.toLowerCase()) {
    case 'create':
      return <Chip color="success" label="Create" size="small" />;
    case 'update':
      return <Chip color="warning" label="Update" size="small" />;
    case 'delete':
      return <Chip color="error" label="Delete" size="small" />;
    default:
      return <Chip color="default" label={action} size="small" />;
  }
};

const TimesheetDetailsModal = ({
  timesheetId,
  isOpen,
  onClose,
}: ModalProps) => {
  const [loading, setLoading] = useState(false);
  const [logsData, setLogsData] = useState<TimesheetLogsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && timesheetId) {
      fetchTimesheetLogs();
    }
  }, [isOpen, timesheetId]);

  const fetchTimesheetLogs = async () => {
    if (!timesheetId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axiosRubyClient.get(
        `/api/timesheets/${timesheetId}/logs`,
      );
      setLogsData(response.data);
    } catch (error) {
      console.error('Error fetching timesheet logs:', error);
      setError('Error loading timesheet logs');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          maxWidth: '1200px',
          width: '95%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title>
            Timesheet Logs {timesheetId ? `- ID: ${timesheetId}` : ''}
          </Title>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <Card.Section>
          <Stack vertical gap={24}>
            {loading && (
              <Stack
                align="center"
                justify="center"
                style={{ padding: '40px 0' }}
              >
                <LoadingState
                  overlayColor="white"
                  text="Loading timesheet logs..."
                />
              </Stack>
            )}

            {error && (
              <div
                style={{ textAlign: 'center', padding: '20px', color: 'red' }}
              >
                <Body>{error}</Body>
              </div>
            )}

            {logsData && !loading && (
              <>
                {/* Timesheet Info */}
                <Stack vertical gap={16}>
                  <Heading>Timesheet Information</Heading>
                  <Stack gap={32} wrap>
                    {logsData.timesheet_info.worker?.name && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">Worker</Body>
                        <Body>{logsData.timesheet_info.worker.name}</Body>
                      </Stack>
                    )}
                    {logsData.timesheet_info.job?.title && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">Shift</Body>
                        <Body>{logsData.timesheet_info.job.title}</Body>
                      </Stack>
                    )}
                    {logsData.timesheet_info.account?.name && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">Account</Body>
                        <Body>{logsData.timesheet_info.account.name}</Body>
                      </Stack>
                    )}
                    {logsData.timesheet_info.current_status && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">Status</Body>
                        <Stack gap={8}>
                          {logsData.timesheet_info.current_status.approved !==
                            undefined && (
                            <Chip
                              color={
                                logsData.timesheet_info.current_status.approved
                                  ? 'success'
                                  : 'default'
                              }
                              label={
                                logsData.timesheet_info.current_status.approved
                                  ? 'Approved'
                                  : 'Pending'
                              }
                              size="small"
                            />
                          )}
                          {logsData.timesheet_info.current_status.invoiced !==
                            undefined && (
                            <Chip
                              color={
                                logsData.timesheet_info.current_status.invoiced
                                  ? 'success'
                                  : 'default'
                              }
                              label={
                                logsData.timesheet_info.current_status.invoiced
                                  ? 'Invoiced'
                                  : 'Not Invoiced'
                              }
                              size="small"
                            />
                          )}
                          {logsData.timesheet_info.current_status.flagged && (
                            <Chip color="error" label="Flagged" size="small" />
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {/* Time Details */}
                {logsData.timesheet_info.time_tracking && (
                  <Stack vertical gap={16}>
                    <Heading>Time Details</Heading>
                    <Stack gap={32} wrap>
                      {/* Original Times */}
                      {logsData.timesheet_info.time_tracking.original && (
                        <Stack vertical gap={12}>
                          <Body weight="medium">Original Times</Body>
                          <Stack vertical gap={4}>
                            {logsData.timesheet_info.time_tracking.original
                              .checkin_at && (
                              <Small>
                                Check-in:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.original
                                    .checkin_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.original
                              .checkout_at && (
                              <Small>
                                Check-out:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.original
                                    .checkout_at,
                                )}
                              </Small>
                            )}
                          </Stack>
                        </Stack>
                      )}

                      {/* Reported Times */}
                      {logsData.timesheet_info.time_tracking.reported && (
                        <Stack vertical gap={12}>
                          <Body weight="medium">Reported Times</Body>
                          <Stack vertical gap={4}>
                            {logsData.timesheet_info.time_tracking.reported
                              .checkin_at && (
                              <Small>
                                Check-in:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.reported
                                    .checkin_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.reported
                              .checkout_at && (
                              <Small>
                                Check-out:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.reported
                                    .checkout_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.reported
                              .break_minutes !== undefined &&
                              logsData.timesheet_info.time_tracking.reported
                                .break_minutes !== null && (
                                <Small>
                                  Break Minutes:{' '}
                                  {
                                    logsData.timesheet_info.time_tracking
                                      .reported.break_minutes
                                  }
                                </Small>
                              )}
                          </Stack>
                        </Stack>
                      )}

                      {/* Modified Times */}
                      {logsData.timesheet_info.time_tracking.modified && (
                        <Stack vertical gap={12}>
                          <Body weight="medium">Modified Times</Body>
                          <Stack vertical gap={4}>
                            {logsData.timesheet_info.time_tracking.modified
                              .checkin_at && (
                              <Small>
                                Check-in:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.modified
                                    .checkin_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.modified
                              .checkout_at && (
                              <Small>
                                Check-out:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.modified
                                    .checkout_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.modified
                              .break_minutes !== undefined &&
                              logsData.timesheet_info.time_tracking.modified
                                .break_minutes !== null && (
                                <Small>
                                  Break Minutes:{' '}
                                  {
                                    logsData.timesheet_info.time_tracking
                                      .modified.break_minutes
                                  }
                                </Small>
                              )}
                          </Stack>
                        </Stack>
                      )}

                      {/* Approved Times */}
                      {logsData.timesheet_info.time_tracking.approved && (
                        <Stack vertical gap={12}>
                          <Body weight="medium">Approved Times</Body>
                          <Stack vertical gap={4}>
                            {logsData.timesheet_info.time_tracking.approved
                              .checkin_at && (
                              <Small>
                                Check-in:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.approved
                                    .checkin_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.approved
                              .checkout_at && (
                              <Small>
                                Check-out:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.approved
                                    .checkout_at,
                                )}
                              </Small>
                            )}
                            {logsData.timesheet_info.time_tracking.approved
                              .break_minutes !== undefined &&
                              logsData.timesheet_info.time_tracking.approved
                                .break_minutes !== null && (
                                <Small>
                                  Break Minutes:{' '}
                                  {
                                    logsData.timesheet_info.time_tracking
                                      .approved.break_minutes
                                  }
                                </Small>
                              )}
                            {logsData.timesheet_info.time_tracking.approved
                              .total_minutes !== undefined &&
                              logsData.timesheet_info.time_tracking.approved
                                .total_minutes !== null && (
                                <Small>
                                  Total Minutes:{' '}
                                  {
                                    logsData.timesheet_info.time_tracking
                                      .approved.total_minutes
                                  }
                                </Small>
                              )}
                            {logsData.timesheet_info.time_tracking.approved
                              .approved_at && (
                              <Small>
                                Approved At:{' '}
                                {formatDate(
                                  logsData.timesheet_info.time_tracking.approved
                                    .approved_at,
                                )}
                              </Small>
                            )}
                          </Stack>
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                )}

                {/* Summary */}
                <Stack vertical gap={16}>
                  <Heading>Change Summary</Heading>
                  <Stack gap={32} wrap>
                    {logsData.summary.total_changes !== undefined &&
                      logsData.summary.total_changes !== null && (
                        <Stack vertical gap={8}>
                          <Body weight="medium">Total Changes</Body>
                          <Body weight="bold">
                            {logsData.summary.total_changes}
                          </Body>
                        </Stack>
                      )}
                    {logsData.summary.recent_changes_24h !== undefined &&
                      logsData.summary.recent_changes_24h !== null && (
                        <Stack vertical gap={8}>
                          <Body weight="medium">Recent Changes (24h)</Body>
                          <Body weight="bold">
                            {logsData.summary.recent_changes_24h}
                          </Body>
                        </Stack>
                      )}
                    {logsData.summary.critical_changes !== undefined &&
                      logsData.summary.critical_changes !== null && (
                        <Stack vertical gap={8}>
                          <Body weight="medium">Critical Changes</Body>
                          <Body
                            weight="bold"
                            color={
                              logsData.summary.critical_changes > 0
                                ? 'danger'
                                : 'success'
                            }
                          >
                            {logsData.summary.critical_changes}
                          </Body>
                        </Stack>
                      )}
                    {logsData.summary.most_changed_column && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">Most Changed Column</Body>
                        <Body>{logsData.summary.most_changed_column}</Body>
                      </Stack>
                    )}
                    {logsData.summary.first_change && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">First Change</Body>
                        <Body>{formatDate(logsData.summary.first_change)}</Body>
                      </Stack>
                    )}
                    {logsData.summary.last_change && (
                      <Stack vertical gap={8}>
                        <Body weight="medium">Last Change</Body>
                        <Body>{formatDate(logsData.summary.last_change)}</Body>
                      </Stack>
                    )}
                  </Stack>
                </Stack>

                {/* Actions Summary */}
                {logsData.summary.changes_by_action &&
                  Object.keys(logsData.summary.changes_by_action).length >
                    0 && (
                    <Stack gap={16} wrap>
                      <Stack vertical gap={8}>
                        <Body weight="medium">Changes by Action</Body>
                        <Stack gap={8}>
                          {logsData.summary.changes_by_action.create !==
                            undefined &&
                            logsData.summary.changes_by_action.create !==
                              null && (
                              <Chip
                                color="success"
                                label={`${logsData.summary.changes_by_action.create} Creates`}
                                size="small"
                              />
                            )}
                          {logsData.summary.changes_by_action.update !==
                            undefined &&
                            logsData.summary.changes_by_action.update !==
                              null && (
                              <Chip
                                color="warning"
                                label={`${logsData.summary.changes_by_action.update} Updates`}
                                size="small"
                              />
                            )}
                        </Stack>
                      </Stack>
                    </Stack>
                  )}

                {/* Logs Table */}
                {logsData.logs && logsData.logs.length > 0 && (
                  <Stack vertical gap={16}>
                    <Heading>
                      Change Log ({logsData.logs.length} entries)
                    </Heading>
                    <Table spacing={false}>
                      <TableHeader fields={LOG_HEADER_FIELDS} />
                      <tbody>
                        {logsData.logs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.timestamp && (
                                  <Small>{formatDate(log.timestamp)}</Small>
                                )}
                              </TextStack>
                            </TableCell>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.action && getActionChip(log.action)}
                              </TextStack>
                            </TableCell>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.column_changed && (
                                  <Small>{log.column_changed}</Small>
                                )}
                              </TextStack>
                            </TableCell>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.change_summary && (
                                  <Small>{log.change_summary}</Small>
                                )}
                              </TextStack>
                            </TableCell>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.changed_by?.name && (
                                  <Small>{log.changed_by.name}</Small>
                                )}
                                {log.changed_by?.type && (
                                  <Small>({log.changed_by.type})</Small>
                                )}
                              </TextStack>
                            </TableCell>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.severity && getSeverityChip(log.severity)}
                              </TextStack>
                            </TableCell>
                            <TableCell>
                              <TextStack spacing="tight">
                                {log.category && <Small>{log.category}</Small>}
                              </TextStack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </tbody>
                    </Table>
                  </Stack>
                )}

                {(!logsData.logs || logsData.logs.length === 0) && (
                  <Stack vertical gap={16}>
                    <Heading>Change Log</Heading>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Body>No logs found for this timesheet.</Body>
                    </div>
                  </Stack>
                )}
              </>
            )}

            <Stack justify="end">
              <Button
                a11yLabel="Close timesheet logs modal"
                appearance="primary"
                label="Close"
                onClick={onClose}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </div>
    </div>
  );
};

export default TimesheetDetailsModal;
